<div class="page-container">
  <app-page-header title="Manage Station Clocks" subtitle="Configure break times and schedules for radio stations"
    [showBackButton]="true" backRoute="/userhome" backLabel="Back to Dashboard">
    <button *ngIf="canDelete" type="button" class="btn-outline btn-bulk-delete btn-compact"
      (click)="openBulkDeleteDialog()" [disabled]="!selectedStation || stationClockLoading"
      title="Bulk delete clocks by day/hour grid">
      <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
        </path>
      </svg>
      Bulk Delete
    </button>
    <button *ngIf="canAdd" type="button" class="btn-primary btn-compact" (click)="openAddDialog()"
      [disabled]="!selectedStation || stationClockLoading">
      <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
      </svg>
      Add Clock
    </button>
  </app-page-header>

  <app-station-header [stations]="stations" [selectedStation]="selectedStation" [loading]="stationLoading"
    title="Select Station" subtitle="Choose a station to manage clocks" (stationChange)="onStationChange($event)">
  </app-station-header>

  <div class="content-wrapper" *ngIf="selectedStation">
    <div class="clocks-toolbar">
      <mat-form-field appearance="outline" class="day-filter-field" subscriptSizing="dynamic">
        <mat-label>Day of Week</mat-label>
        <mat-select [(ngModel)]="selectedDayFilter" (selectionChange)="onDayFilterChange()"
          panelClass="day-filter-panel">
          <mat-option [value]="''">All Days</mat-option>
          <mat-option *ngFor="let day of dayOptions" [value]="day.value">{{ day.label }}</mat-option>
        </mat-select>
      </mat-form-field>
      <button type="button" class="btn-icon-refresh" (click)="refreshData()"
        [disabled]="stationClockLoading" title="Refresh table" aria-label="Refresh table">
        <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15">
          </path>
        </svg>
      </button>
    </div>

    <div *ngIf="isDaySpecific && selectedClockIds.size > 0" class="bulk-action-bar">
      <span class="bulk-selection-count">{{ selectedClockIds.size }} selected</span>
      <div class="bulk-action-buttons">
        <button type="button" class="btn-primary btn-sm" (click)="openMassChange()" [disabled]="bulkSubmitting">
          Mass Change
        </button>
        <button type="button" class="btn-danger btn-sm" (click)="deleteSelected()" [disabled]="bulkSubmitting">
          Delete Selected
        </button>
      </div>

      <div *ngIf="showMassChangeForm" class="mass-change-form">
        <label class="form-label" for="mass_change_minute">New break minute (0-59)</label>
        <div class="mass-change-controls">
          <input type="number" id="mass_change_minute" class="form-input mass-change-input" appNoScrollInput
            [(ngModel)]="massChangeMinute" min="0" max="59" placeholder="0-59">
          <button type="button" class="btn-primary btn-sm" (click)="applyMassChange()" [disabled]="bulkSubmitting">
            <span *ngIf="bulkSubmitting" class="btn-spinner"></span>
            Apply to {{ selectedClockIds.size }} selected
          </button>
          <button type="button" class="btn-secondary btn-sm" (click)="cancelMassChange()" [disabled]="bulkSubmitting">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <app-common-table [data]="stationClocks" [columns]="tableColumns" [actions]="tableActions"
      [loading]="stationClockLoading" emptyMessage="No station clocks found"
      emptyDescription="Get started by adding break times for this station." [showAddButton]="canAdd"
      addButtonText="Add New Clock" (addClick)="openAddDialog()">

      <ng-template #headerTemplate>
        <th *ngIf="isDaySpecific" class="th-select">
          <label class="row-check">
            <input type="checkbox" [checked]="allVisibleSelected" [indeterminate]="someVisibleSelected"
              (change)="toggleSelectAll()" aria-label="Select all clocks">
            <span class="row-check-box" aria-hidden="true"></span>
          </label>
        </th>
        <th class="th-day">Day</th>
        <th class="th-time">Break Start Time</th>
        <th class="th-length">Break Length (min)</th>
        <th class="th-actions">Actions</th>
      </ng-template>

      <ng-template #rowTemplate let-clock>
        <tr class="table-row" [class.selected-row]="isClockSelected(clock)">
          <td *ngIf="isDaySpecific" class="td-select">
            <label class="row-check">
              <input type="checkbox" [checked]="isClockSelected(clock)" (change)="toggleClockSelection(clock)"
                (click)="$event.stopPropagation()" [attr.aria-label]="'Select clock ' + getClockId(clock)">
              <span class="row-check-box" aria-hidden="true"></span>
            </label>
          </td>
          <td class="td-day">{{ clock.dayName }}</td>
          <td class="td-time">{{ formatTime(clock.break_start_time) }}</td>
          <td class="td-length">{{ clock.break_length }}</td>
          <td class="td-actions">
            <div class="action-buttons">
              <ng-container *ngFor="let action of tableActions">
                <button *ngIf="!action.show || action.show(clock)" type="button" [ngClass]="action.class"
                  (click)="action.callback(clock)" [title]="action.label" [attr.aria-label]="action.label">
                  <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path *ngIf="action.icon === 'edit'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                    </path>
                    <path *ngIf="action.icon === 'delete'" stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                    </path>
                  </svg>
                </button>
              </ng-container>
            </div>
          </td>
        </tr>
      </ng-template>
    </app-common-table>

    <div *ngIf="!isDaySpecific && totalPages > 1" class="pagination">
      <button type="button" class="pagination-btn" [class.disabled]="currentPage === 1"
        (click)="goToPage(currentPage - 1)" [disabled]="currentPage === 1">
        Previous
      </button>

      <div class="pagination-numbers">
        <button *ngFor="let page of getPaginationPages()" type="button" class="pagination-number"
          [class.active]="page === currentPage" (click)="goToPage(page)">
          {{ page }}
        </button>
      </div>

      <button type="button" class="pagination-btn" [class.disabled]="currentPage === totalPages"
        (click)="goToPage(currentPage + 1)" [disabled]="currentPage === totalPages">
        Next
      </button>
    </div>
  </div>

  <div class="no-station-card" *ngIf="!selectedStation && !stationLoading && stations.length === 0">
    <div class="no-station-content">
      <svg class="no-station-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <h3>Select a Station</h3>
      <p>Use the <strong>Select Station</strong> control in the bar above to choose a station, then manage its clocks
        here.</p>
    </div>
  </div>
</div>

<!-- Add / Edit dialog -->
<div *ngIf="isDialogOpen" class="modal-overlay" (click)="closeDialog()">
  <div class="modal-content modal-content--wide" (click)="$event.stopPropagation()" role="dialog"
    [attr.aria-label]="(isEditMode ? 'Edit' : 'Add') + ' Station Clock'">
    <div class="modal-header">
      <div>
        <h2 class="modal-title">{{ isEditMode ? 'Edit' : 'Add' }} Station Clock</h2>
        <p class="modal-subtitle" *ngIf="selectedStation">
          {{ selectedStation.call_sign }} · {{ selectedStation.name }}
        </p>
      </div>
      <button type="button" class="modal-close" (click)="closeDialog()" aria-label="Close dialog">
        <svg class="icon" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"></path>
        </svg>
      </button>
    </div>

    <form [formGroup]="clockForm" (ngSubmit)="onSubmit()" class="modal-form">
      <div class="form-row">
        <div class="form-group form-group--grow">
          <label class="form-label" for="stationid">Station *</label>
          <select id="stationid" class="form-input" formControlName="stationid">
            <option value="">Select a station</option>
            <option *ngFor="let station of stations" [value]="station.id">
              {{ station.call_sign }} - {{ station.name }}
            </option>
          </select>
          <div *ngIf="clockForm.get('stationid')?.hasError('required') && clockForm.get('stationid')?.touched"
            class="form-error">
            Station is required
          </div>
        </div>

        <div class="form-group">
          <label class="form-label" for="break_length">Break Length (min) *</label>
          <input type="number" id="break_length" class="form-input" formControlName="break_length" min="1"
            appNoScrollInput placeholder="e.g. 10">
          <div *ngIf="clockForm.get('break_length')?.hasError('required') && clockForm.get('break_length')?.touched"
            class="form-error">
            Break length is required
          </div>
          <div *ngIf="clockForm.get('break_length')?.hasError('min') && clockForm.get('break_length')?.touched"
            class="form-error">
            Must be at least 1 minute
          </div>
        </div>

        <div class="form-group">
          <label class="form-label" for="break_start_minute">Start Minute (0–59) *</label>
          <input type="number" id="break_start_minute" class="form-input" formControlName="break_start_minute" min="0"
            max="59" appNoScrollInput placeholder="0–59">
          <div
            *ngIf="clockForm.get('break_start_minute')?.hasError('required') && clockForm.get('break_start_minute')?.touched"
            class="form-error">
            Minute is required
          </div>
          <div
            *ngIf="clockForm.get('break_start_minute')?.hasError('min') || clockForm.get('break_start_minute')?.hasError('max')"
            class="form-error">
            Minute must be between 0 and 59
          </div>
        </div>
      </div>

      <div class="time-grid-section">
        <div class="time-grid-heading">
          <h3>Select Break Times</h3>
          <p class="grid-instructions">
            Click cells to toggle. Click a day or hour header to select that entire row or column.
          </p>
        </div>

        <div class="time-grid-container">
          <table class="time-grid">
            <thead>
              <tr>
                <th class="corner-cell" scope="col">Day / Hour</th>
                <th *ngFor="let hour of hours; let hourIndex = index" class="hour-header" scope="col"
                  (click)="onHourHeaderClick(hourIndex)" [attr.title]="'Toggle all ' + hour">
                  {{ hour }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let day of days; let dayIndex = index">
                <th class="day-header" scope="row" (click)="onDayHeaderClick(dayIndex)"
                  [attr.title]="'Toggle all ' + day.label">
                  {{ day.label }}
                </th>
                <td *ngFor="let hour of hours; let hourIndex = index" class="time-cell"
                  [class.time-cell--on]="timeGrid[dayIndex][hourIndex]">
                  <button type="button" class="grid-toggle"
                    [class.grid-toggle--on]="timeGrid[dayIndex][hourIndex]"
                    (click)="onTimeGridChange(dayIndex, hourIndex)"
                    [attr.aria-pressed]="timeGrid[dayIndex][hourIndex]"
                    [attr.aria-label]="day.label + ' ' + hour">
                    <span class="grid-toggle-mark" aria-hidden="true"></span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="modal-actions">
        <button type="button" class="btn-secondary" (click)="closeDialog()">Cancel</button>
        <button type="submit" class="btn-primary" [disabled]="!clockForm.valid || dialogSubmitting">
          <span *ngIf="dialogSubmitting" class="btn-spinner"></span>
          {{ dialogSubmitting ? 'Processing...' : (isEditMode ? 'Update' : 'Create') }}
        </button>
      </div>
    </form>
  </div>
</div>

<!-- Bulk Delete dialog -->
<div *ngIf="isBulkDeleteOpen" class="modal-overlay" (click)="closeBulkDeleteDialog()">
  <div class="modal-content modal-content--wide" (click)="$event.stopPropagation()" role="dialog"
    aria-label="Bulk Delete Clocks">
    <div class="modal-header">
      <div>
        <h2 class="modal-title">Bulk Delete Clocks</h2>
        <p class="modal-subtitle">Select day/hour slots to remove for the station</p>
      </div>
      <button type="button" class="modal-close" (click)="closeBulkDeleteDialog()" aria-label="Close dialog">
        <svg class="icon" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"></path>
        </svg>
      </button>
    </div>

    <div class="modal-form">
      <div class="form-row">
        <div class="form-group form-group--grow">
          <label class="form-label" for="bulk_delete_stationid">Station *</label>
          <select id="bulk_delete_stationid" class="form-input" [(ngModel)]="bulkDeleteStationId"
            (ngModelChange)="onBulkDeleteStationChange()">
            <option *ngFor="let station of stations" [ngValue]="station.id">
              {{ station.call_sign }} - {{ station.name }}
            </option>
          </select>
        </div>
      </div>

      <div class="time-grid-section">
        <div class="time-grid-heading">
          <h3>Clocks to delete</h3>
          <p class="grid-instructions">
            Filled cells have an existing clock. Toggle cells (or day/hour headers) to choose what to delete.
          </p>
        </div>

        <div *ngIf="bulkDeleteLoading" class="bulk-grid-loading">
          <span class="spinner-small spinner-dark"></span>
          <span>Loading existing clocks…</span>
        </div>

        <div class="time-grid-container" *ngIf="!bulkDeleteLoading">
          <table class="time-grid">
            <thead>
              <tr>
                <th class="corner-cell" scope="col">Day / Hour</th>
                <th *ngFor="let hour of hours; let hourIndex = index" class="hour-header" scope="col"
                  (click)="onBulkDeleteHourHeaderClick(hourIndex)" [attr.title]="'Toggle all ' + hour">
                  {{ hour }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let day of days; let dayIndex = index">
                <th class="day-header" scope="row" (click)="onBulkDeleteDayHeaderClick(dayIndex)"
                  [attr.title]="'Toggle all ' + day.label">
                  {{ day.label }}
                </th>
                <td *ngFor="let hour of hours; let hourIndex = index" class="time-cell"
                  [class.time-cell--on]="bulkDeleteGrid[dayIndex][hourIndex]"
                  [class.time-cell--delete]="bulkDeleteGrid[dayIndex][hourIndex]">
                  <button type="button" class="grid-toggle grid-toggle--danger"
                    [class.grid-toggle--on]="bulkDeleteGrid[dayIndex][hourIndex]"
                    (click)="onBulkDeleteGridChange(dayIndex, hourIndex)"
                    [attr.aria-pressed]="bulkDeleteGrid[dayIndex][hourIndex]"
                    [attr.aria-label]="'Delete ' + day.label + ' ' + hour">
                    <span class="grid-toggle-mark" aria-hidden="true"></span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="modal-actions">
        <button type="button" class="btn-secondary" (click)="closeBulkDeleteDialog()">Cancel</button>
        <button type="button" class="btn-danger" (click)="submitBulkDelete()"
          [disabled]="bulkDeleteLoading || bulkDeleteSubmitting || bulkDeleteSelectionCount === 0">
          <span *ngIf="bulkDeleteSubmitting" class="btn-spinner"></span>
          {{ bulkDeleteSubmitting ? 'Deleting...' : 'Delete Checked' + (bulkDeleteSelectionCount > 0 ? ' (' +
          bulkDeleteSelectionCount + ')' : '') }}
        </button>
      </div>
    </div>
  </div>
</div>
